import { ReactNode, useContext, useState } from "react"
import { useCanvasPainter } from "../hooks/useCanvasPainter.tsx"
import { useEventListener } from "../hooks/useEventListener.ts"
import { Rect } from "../Rect.tsx"
import { doubleStroke } from "./GraphicsUtilities.tsx"
import { Keyframes } from "./Keyframe.tsx"
import { Playhead } from "./Playhead.tsx"
import { Timeline } from "./Timeline.ts"
import { AppActionContext } from "../AppState.ts"

export function TimelineView(props: {
  timeline: Timeline,
  playheadTime: number,
}): ReactNode {
  const { timeline, playheadTime } = props

  const [element, setElement] = useState<HTMLDivElement | null>(null)
  const [playheadGraphic, setPlayheadGraphic] = useState<HTMLElement | null>(null)

  const isDragging = usePlayheadDragging({
    timelineElement: element,
    playhead: playheadGraphic,
    playheadTime: playheadTime,
    timelineDuration: timeline.duration,
  })

  return <div
    ref={setElement}
    style={{
      marginLeft: '10px',
      marginRight: '10px',
      position: 'relative',
      display: 'flex',
      height: '30px',

      cursor: isDragging ? 'col-resize' : 'default',
    }}>
    <Track />
    <Keyframes timeline={timeline} />
    <Playhead
      timeline={timeline}
      playheadTime={playheadTime}
      setPlayheadGraphic={setPlayheadGraphic}
    />
  </div>
}

function usePlayheadDragging(props: {
  timelineElement: HTMLElement | null,
  playhead: HTMLElement | null,
  playheadTime: number,
  timelineDuration: number,
}): boolean {
  const { timelineElement, playhead, playheadTime, timelineDuration } = props

  const sendAppAction = useContext(AppActionContext)
  const [priorClientX, setPriorClientX] = useState<number | null>(null)

  useEventListener('pointerdown', timelineElement, event => {
    if (!timelineElement || !playhead || event.button !== 0) {
      return
    }

    if (event.target !== playhead) {
      const x = event.clientX - timelineElement.getBoundingClientRect().left
      const time = x / timelineElement.clientWidth * timelineDuration
      sendAppAction({
        type: 'setPlayheadTime',
        time,
      })
    }

    setPriorClientX(event.clientX)
    timelineElement.setPointerCapture(event.pointerId)
  })

  useEventListener('pointermove', priorClientX === null ? null : timelineElement, event => {
    if (priorClientX === null || !timelineElement) {
      return
    }

    const newTime = playheadTime + (event.clientX - priorClientX) / timelineElement.clientWidth * timelineDuration
    setPriorClientX(event.clientX)
    sendAppAction({
      type: 'setPlayheadTime',
      time: newTime
    })
  })

  useEventListener('pointerup', timelineElement, () => {
    setPriorClientX(null)
  })

  return priorClientX !== null
}

function Track(): ReactNode {
  const [canvas, setCanvas] = useState<HTMLCanvasElement | null>(null)

  const lineWidth = 1
  const padding = 2 * lineWidth

  useCanvasPainter(canvas, { alpha: true }, (gc, canvas) => {
    gc.lineCap = 'round'
    gc.lineJoin = 'round'
    gc.miterLimit = 100

    const rect = new Rect(
      0, 0,
      canvas.clientWidth, canvas.clientHeight
    ).insetBy(padding)

    gc.moveTo(...rect.topLeft.tuple)
    gc.lineTo(...rect.bottomLeft.tuple)
    gc.moveTo(...rect.middleLeft.tuple)
    gc.lineTo(...rect.middleRight.tuple)
    gc.moveTo(...rect.topRight.tuple)
    gc.lineTo(...rect.bottomRight.tuple)

    doubleStroke(gc, lineWidth, { inner: 'gray' })
  }, [])

  return <canvas
    ref={setCanvas}
    style={{
      position: 'absolute',
      left: `${-padding}px`,
      right: `${-padding}px`,
      width: `calc(100% + ${2 * padding}px)`,
      marginTop: '15px',
      height: '15px',
      // border: '1px solid red',
      alignSelf: 'center',
    }}
  />
}
